// src/pages/driverReports/assignmentCostPlanHistory.js
// Month-versioned driver assignment cost plans.
//
// The real driver-car assignment remains unchanged. Only the cost/plan terms are
// versioned by statement month so rebuilding an old car sheet uses the plan that
// was valid in that month.

import { doc, runTransaction, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isValidMonth(value) {
  return MONTH_RE.test(String(value || "").trim());
}

export function monthFromDate(value) {
  try {
    if (!value) return "";
    if (typeof value?.toDate === "function") value = value.toDate();
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

export function previousMonth(month) {
  if (!isValidMonth(month)) return "";
  const [year, monthNo] = month.split("-").map(Number);
  const d = new Date(year, monthNo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function currentMonthText() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthStartDate(month) {
  if (!isValidMonth(month)) return null;
  const [year, monthNo] = month.split("-").map(Number);
  return new Date(year, monthNo - 1, 1, 0, 0, 0, 0);
}

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function maxDate(a, b) {
  const da = toDate(a);
  const db = toDate(b);
  if (!da) return db;
  if (!db) return da;
  return da.getTime() >= db.getTime() ? da : db;
}

function normalizeRole(value) {
  return String(value || "main").toLowerCase() === "second" ? "second" : "main";
}

function normalizePlan(plan = {}) {
  const effectiveFromMonth = String(plan.effectiveFromMonth || "").trim();
  const effectiveToMonth = String(plan.effectiveToMonth || "").trim();
  const driverRole = normalizeRole(plan.driverRole);
  const costConfigured = plan.costConfigured === true;
  const installmentPlanEnabled =
    driverRole === "main" && costConfigured && plan.installmentPlanEnabled === true;

  return {
    effectiveFromMonth,
    effectiveToMonth: isValidMonth(effectiveToMonth) ? effectiveToMonth : null,
    driverRole,
    costConfigured,
    installmentMode: costConfigured ? "auto" : "",
    monthlyInstallment:
      driverRole === "main" && costConfigured ? Number(plan.monthlyInstallment || 0) : 0,
    secondDriverMonthlyExpense:
      driverRole === "second" && costConfigured
        ? Number(plan.secondDriverMonthlyExpense || 500)
        : 500,
    installmentPlanEnabled,
    installmentCurrentNo: installmentPlanEnabled
      ? Number(plan.installmentCurrentNo || 0)
      : 0,
    installmentTotalCount: installmentPlanEnabled
      ? Number(plan.installmentTotalCount || 0)
      : 0,
    installmentBaseMonth: installmentPlanEnabled
      ? String(plan.installmentBaseMonth || "").trim()
      : "",
    createdAt: plan.createdAt || null,
    updatedAt: plan.updatedAt || null,
  };
}

function snapshotFromAssignment(assignment, effectiveFromMonth, effectiveToMonth = null) {
  const role = normalizeRole(
    assignment?.driverRole || assignment?.assignmentRole || assignment?.role || "main"
  );
  const configured =
    assignment?.costConfigured === true || assignment?.installmentMode === "auto";
  const planEnabled =
    role === "main" &&
    configured &&
    (assignment?.installmentPlanEnabled === true ||
      Number(assignment?.installmentTotalCount || 0) > 0 ||
      Number(assignment?.installmentCurrentNo || 0) > 0);

  return normalizePlan({
    effectiveFromMonth,
    effectiveToMonth,
    driverRole: role,
    costConfigured: configured,
    monthlyInstallment: Number(assignment?.monthlyInstallment || 0),
    secondDriverMonthlyExpense: Number(assignment?.secondDriverMonthlyExpense || 500),
    installmentPlanEnabled: planEnabled,
    installmentCurrentNo: Number(assignment?.installmentCurrentNo || 0),
    installmentTotalCount: Number(assignment?.installmentTotalCount || 0),
    installmentBaseMonth:
      assignment?.installmentBaseMonth ||
      monthFromDate(assignment?.installmentEffectiveFrom || assignment?.startDate) ||
      effectiveFromMonth,
  });
}

function hasLegacyConfiguredCost(assignment) {
  return (
    assignment?.costConfigured === true ||
    assignment?.installmentMode === "auto" ||
    Number(assignment?.monthlyInstallment || 0) > 0 ||
    (normalizeRole(assignment?.driverRole) === "second" &&
      Number(assignment?.secondDriverMonthlyExpense || 0) > 0)
  );
}

export function getAssignmentCostPlanHistory(assignment) {
  const history = Array.isArray(assignment?.costPlanHistory)
    ? assignment.costPlanHistory
        .map(normalizePlan)
        .filter((plan) => isValidMonth(plan.effectiveFromMonth))
    : [];

  history.sort((a, b) => a.effectiveFromMonth.localeCompare(b.effectiveFromMonth));
  return history;
}

export function getAssignmentCostPlanForMonth(assignment, month) {
  if (!isValidMonth(month)) return null;
  const history = getAssignmentCostPlanHistory(assignment);
  if (!history.length) return null;

  const matches = history.filter(
    (plan) =>
      plan.effectiveFromMonth <= month &&
      (!plan.effectiveToMonth || plan.effectiveToMonth >= month)
  );

  if (!matches.length) return null;
  return matches[matches.length - 1];
}

// Returns an assignment-shaped object for the selected statement month.
// Old assignment documents without costPlanHistory keep the old behavior.
export function applyAssignmentCostPlanForMonth(assignment, month) {
  const history = getAssignmentCostPlanHistory(assignment);
  if (!history.length) return assignment;

  const plan = getAssignmentCostPlanForMonth(assignment, month);

  // A history exists, but no plan covers this month. That means this month is
  // intentionally before the first configured plan, so old copied/fetched
  // expense logic must remain active.
  if (!plan) {
    return {
      ...assignment,
      costConfigured: false,
      installmentMode: "",
      monthlyInstallment: 0,
      secondDriverMonthlyExpense: 500,
      installmentPlanEnabled: false,
      installmentCurrentNo: 0,
      installmentTotalCount: 0,
      installmentBaseMonth: "",
      activeCostPlan: null,
      costPlanHistoryApplied: true,
    };
  }

  const planStart = monthStartDate(plan.effectiveFromMonth);
  const effectiveStart = maxDate(assignment?.startDate, planStart);

  return {
    ...assignment,
    driverRole: plan.driverRole,
    costConfigured: plan.costConfigured,
    installmentMode: plan.installmentMode,
    monthlyInstallment: plan.monthlyInstallment,
    secondDriverMonthlyExpense: plan.secondDriverMonthlyExpense,
    installmentPlanEnabled: plan.installmentPlanEnabled,
    installmentCurrentNo: plan.installmentCurrentNo,
    installmentTotalCount: plan.installmentTotalCount,
    installmentBaseMonth: plan.installmentBaseMonth,
    installmentEffectiveFrom: effectiveStart || assignment?.installmentEffectiveFrom || assignment?.startDate,
    activeCostPlan: plan,
    costPlanHistoryApplied: true,
  };
}

function recomputePlanEnds(history) {
  const sorted = [...history]
    .map(normalizePlan)
    .filter((plan) => isValidMonth(plan.effectiveFromMonth))
    .sort((a, b) => a.effectiveFromMonth.localeCompare(b.effectiveFromMonth));

  return sorted.map((plan, index) => ({
    ...plan,
    effectiveToMonth:
      index < sorted.length - 1 ? previousMonth(sorted[index + 1].effectiveFromMonth) : null,
  }));
}

function currentTopLevelPatch(assignment, history) {
  const nowMonth = currentMonthText();
  const withHistory = { ...assignment, costPlanHistory: history };
  const currentPlan = getAssignmentCostPlanForMonth(withHistory, nowMonth);
  if (!currentPlan) return {};

  const planStart = monthStartDate(currentPlan.effectiveFromMonth);
  const effectiveStart = maxDate(assignment?.startDate, planStart);

  return {
    driverRole: currentPlan.driverRole,
    costConfigured: currentPlan.costConfigured,
    installmentMode: currentPlan.installmentMode,
    monthlyInstallment: currentPlan.monthlyInstallment,
    secondDriverMonthlyExpense: currentPlan.secondDriverMonthlyExpense,
    installmentEffectiveFrom: effectiveStart ? Timestamp.fromDate(effectiveStart) : null,
    installmentPlanEnabled: currentPlan.installmentPlanEnabled,
    installmentCurrentNo: currentPlan.installmentCurrentNo,
    installmentTotalCount: currentPlan.installmentTotalCount,
    installmentBaseMonth: currentPlan.installmentBaseMonth,
  };
}

// Save or replace one month-based cost-plan revision while preserving every
// earlier/later period. The driver-car assignment dates are never changed.
export async function saveAssignmentCostPlanRevision({
  assignmentId,
  effectiveFromMonth,
  values,
}) {
  if (!assignmentId) throw new Error("Missing assignment ID.");
  if (!isValidMonth(effectiveFromMonth)) {
    throw new Error("Please select a valid effective month.");
  }

  const assignmentRef = doc(db, "driverCarAssignments", assignmentId);

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(assignmentRef);
    if (!snap.exists()) throw new Error("Driver assignment was not found.");

    const assignment = { id: snap.id, ...snap.data() };
    const assignmentStartMonth = monthFromDate(assignment.startDate);
    const assignmentEndMonth = monthFromDate(assignment.endDate);

    if (assignmentStartMonth && effectiveFromMonth < assignmentStartMonth) {
      throw new Error(`Effective month cannot be before assignment start (${assignmentStartMonth}).`);
    }
    if (assignmentEndMonth && effectiveFromMonth > assignmentEndMonth) {
      throw new Error(`Effective month cannot be after assignment end (${assignmentEndMonth}).`);
    }

    let history = getAssignmentCostPlanHistory(assignment);
    const now = Timestamp.now();

    // First month-versioned edit of an old assignment: preserve the existing
    // setup as a historical period before the new revision begins.
    if (
      hasLegacyConfiguredCost(assignment) &&
      assignmentStartMonth &&
      effectiveFromMonth > assignmentStartMonth &&
      !history.some((plan) => plan.effectiveFromMonth < effectiveFromMonth)
    ) {
      history.push({
        ...snapshotFromAssignment(
          assignment,
          assignmentStartMonth,
          previousMonth(effectiveFromMonth)
        ),
        createdAt: now,
        updatedAt: now,
      });
    }

    const role = normalizeRole(values?.driverRole);
    const configured = Boolean(values?.costConfigured);
    const planEnabled =
      role === "main" && configured && Boolean(values?.installmentPlanEnabled);

    const newPlan = normalizePlan({
      effectiveFromMonth,
      driverRole: role,
      costConfigured: configured,
      monthlyInstallment: Number(values?.monthlyInstallment || 0),
      secondDriverMonthlyExpense: Number(values?.secondDriverMonthlyExpense || 500),
      installmentPlanEnabled: planEnabled,
      installmentCurrentNo: planEnabled ? Number(values?.installmentCurrentNo || 0) : 0,
      installmentTotalCount: planEnabled ? Number(values?.installmentTotalCount || 0) : 0,
      installmentBaseMonth: planEnabled
        ? String(values?.installmentBaseMonth || "").trim()
        : "",
      createdAt: now,
      updatedAt: now,
    });

    const existingIndex = history.findIndex(
      (plan) => plan.effectiveFromMonth === effectiveFromMonth
    );

    if (existingIndex >= 0) {
      newPlan.createdAt = history[existingIndex].createdAt || now;
      history[existingIndex] = newPlan;
    } else {
      history.push(newPlan);
    }

    history = recomputePlanEnds(history);

    const patch = {
      costPlanHistory: history,
      costPlanHistoryEnabled: true,
      costPlanHistoryUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...currentTopLevelPatch(assignment, history),
    };

    transaction.update(assignmentRef, patch);

    return history;
  });
}
